import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from 'config/api';
import { useAuthenticatedApiCall } from 'services/api.service';

const RETRY_INTERVAL = 5000;
const REFRESH_INTERVAL = 60000;
const INTERACTIVE_AUTH_ERROR_CODES = new Set(['login_required', 'consent_required', 'interaction_required']);
const INTERACTIVE_AUTH_ERROR_DESCRIPTIONS = ['external interaction required', 'multifactor authentication required'];

interface ImageCookieState {
  previousToken: string | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  abortController: AbortController | null;
}

const getErrorValue = (error: unknown, key: string): string | undefined => {
  if (typeof error !== 'object' || error === null || !(key in error)) {
    return undefined;
  }

  const value = (error as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
};

const requiresInteractiveAuthentication = (error: unknown): boolean => {
  const errorCode = getErrorValue(error, 'error');
  if (errorCode && INTERACTIVE_AUTH_ERROR_CODES.has(errorCode)) {
    return true;
  }

  const description =
    `${getErrorValue(error, 'error_description') ?? ''} ${getErrorValue(error, 'message') ?? ''}`.toLowerCase();
  return INTERACTIVE_AUTH_ERROR_DESCRIPTIONS.some(interactiveDescription =>
    description.includes(interactiveDescription)
  );
};

export const useImageCookie = () => {
  const baseConfig = useMemo(
    () => ({
      baseURL: api.IMAGE_SERVER_URL,
    }),
    []
  );

  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall(baseConfig);
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [isImageCookieReady, setIsImageCookieReady] = useState(false);

  const stateRef = useRef<ImageCookieState>({
    previousToken: null,
    retryTimer: null,
    abortController: null,
  });

  const scheduleRetry = useCallback((attempt: () => Promise<void>, intervalMs: number) => {
    const state = stateRef.current;
    if (state.retryTimer !== null) {
      clearTimeout(state.retryTimer);
    }
    state.retryTimer = setTimeout(() => {
      state.retryTimer = null;
      void attempt();
    }, intervalMs);
  }, []);

  const setImageCookie = useCallback(async () => {
    const state = stateRef.current;

    if (state.abortController) {
      state.abortController.abort();
    }
    const controller = new AbortController();
    state.abortController = controller;

    let currentToken: string;
    try {
      currentToken = await getAccessTokenSilently();
    } catch (error) {
      if (!requiresInteractiveAuthentication(error)) {
        scheduleRetry(setImageCookie, RETRY_INTERVAL);
      }
      return;
    }

    const response = await makeAuthenticatedApiCall<void>({
      url: 'set-image-cookie',
      method: 'POST',
      data: currentToken,
      headers: {
        'Content-Type': 'text/plain',
      },
      withCredentials: true,
      signal: controller.signal,
    });

    if (controller.signal.aborted) return;

    if (!response.error) {
      state.previousToken = currentToken;
      setIsImageCookieReady(true);
      scheduleRetry(setImageCookie, REFRESH_INTERVAL);
    } else {
      scheduleRetry(setImageCookie, RETRY_INTERVAL);
    }
  }, [makeAuthenticatedApiCall, getAccessTokenSilently, scheduleRetry]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    void setImageCookie();

    return () => {
      const state = stateRef.current;
      if (state.retryTimer !== null) {
        clearTimeout(state.retryTimer);
        state.retryTimer = null;
      }
      if (state.abortController) {
        state.abortController.abort();
        state.abortController = null;
      }
    };
  }, [isLoading, isAuthenticated, setImageCookie]);

  return { setImageCookie, isImageCookieReady };
};
